/**
 * OBB (Oriented Bounding Box) 衝突判定ユーティリティ
 * 回転を考慮した3D空間での家具同士、家具と部屋の境界の干渉をチェック
 */

/**
 * 回転を考慮した家具の4つの角の座標を取得（XZ平面）
 * @param {Object} furniture - { position: {x, z}, size: {width, depth}, rotation }
 * @returns {Array} - 4つの角の座標 [{x, z}, ...]
 */
function getRotatedCorners(furniture) {
    const { position, size, rotation = 0 } = furniture
    const halfWidth = size.width / 2
    const halfDepth = size.depth / 2
    const angle = (rotation * Math.PI) / 180 // ラジアンに変換

    // 回転前の4つの角（ローカル座標）
    const corners = [
        { x: -halfWidth, z: -halfDepth }, // 左手前
        { x: halfWidth, z: -halfDepth },  // 右手前
        { x: halfWidth, z: halfDepth },   // 右奥
        { x: -halfWidth, z: halfDepth },  // 左奥
    ]

    // 回転行列を適用してワールド座標に変換
    return corners.map(corner => ({
        x: position.x + corner.x * Math.cos(angle) - corner.z * Math.sin(angle),
        z: position.z + corner.x * Math.sin(angle) + corner.z * Math.cos(angle),
    }))
}

/**
 * 2点から法線ベクトルを計算
 * @param {Object} p1 - {x, z}
 * @param {Object} p2 - {x, z}
 * @returns {Object} - 正規化された法線ベクトル {x, z}
 */
function getNormal(p1, p2) {
    const dx = p2.x - p1.x
    const dz = p2.z - p1.z
    const length = Math.sqrt(dx * dx + dz * dz)
    // 辺のベクトルに垂直な法線（90度回転）
    return { x: -dz / length, z: dx / length }
}

/**
 * 内積を計算
 * @param {Object} v1 - {x, z}
 * @param {Object} v2 - {x, z}
 * @returns {number}
 */
function dotProduct(v1, v2) {
    return v1.x * v2.x + v1.z * v2.z
}

/**
 * 角の座標を軸に投影
 * @param {Array} corners - [{x, z}, ...]
 * @param {Object} axis - {x, z}
 * @returns {Object} - {min, max}
 */
function projectOntoAxis(corners, axis) {
    let min = Infinity
    let max = -Infinity

    corners.forEach(corner => {
        const projection = dotProduct(corner, axis)
        min = Math.min(min, projection)
        max = Math.max(max, projection)
    })

    return { min, max }
}

/**
 * SAT（Separating Axis Theorem）を使用したOBB衝突判定
 * @param {Object} furniture1 - { position, size, rotation }
 * @param {Object} furniture2 - { position, size, rotation }
 * @returns {boolean} - 衝突している場合true
 */
function checkOBBCollision(furniture1, furniture2) {
    const corners1 = getRotatedCorners(furniture1)
    const corners2 = getRotatedCorners(furniture2)

    // 各矩形の辺の法線ベクトルを分離軸として使用
    const axes = [
        // furniture1の2つの辺の法線
        getNormal(corners1[0], corners1[1]),
        getNormal(corners1[1], corners1[2]),
        // furniture2の2つの辺の法線
        getNormal(corners2[0], corners2[1]),
        getNormal(corners2[1], corners2[2]),
    ]

    // 接触状態（端が一致）を干渉とみなさないための許容誤差
    // スナップでぴったり配置した場合も干渉なしと判定
    const epsilon = 0.1

    // 全ての分離軸で投影が重なっているかチェック
    for (const axis of axes) {
        const projection1 = projectOntoAxis(corners1, axis)
        const projection2 = projectOntoAxis(corners2, axis)

        // 投影が重なっていない = 衝突していない
        // epsilonを超えて離れている場合は衝突なし（接触＝干渉なし）
        if (projection1.max <= projection2.min + epsilon || projection2.max <= projection1.min + epsilon) {
            return false
        }
    }

    // 全ての軸で重なっている = 衝突している
    return true
}

/**
 * 2つの箱が衝突しているかチェック（回転を考慮）
 * @param {Object} box1 - { position: {x, y, z}, size: {width, height, depth}, rotation }
 * @param {Object} box2 - { position: {x, y, z}, size: {width, height, depth}, rotation }
 * @returns {boolean} - 衝突している場合true
 */
export function checkCollision(box1, box2) {
    // Y軸方向の衝突チェック（高さ）
    const box1MinY = box1.position.y
    const box1MaxY = box1.position.y + box1.size.height
    const box2MinY = box2.position.y
    const box2MaxY = box2.position.y + box2.size.height

    // Y軸で重なっていない場合は衝突していない
    if (box1MinY >= box2MaxY || box1MaxY <= box2MinY) {
        return false
    }

    // XZ平面での衝突チェック（回転を考慮）
    return checkOBBCollision(box1, box2)
}

/**
 * 家具が部屋の境界内に収まっているかチェック（回転を考慮）
 * @param {Object} furniture - { position: {x, y, z}, size: {width, height, depth}, rotation }
 * @param {Object} room - { width, height, depth }
 * @returns {boolean} - 境界からはみ出している場合true
 */
export function checkRoomBoundary(furniture, room) {
    // Y軸方向のチェック
    if (furniture.position.y < 0 || furniture.position.y + furniture.size.height > room.height) {
        return true
    }

    // XZ平面での境界チェック（回転を考慮）
    const corners = getRotatedCorners(furniture)
    const roomMin = { x: -room.width / 2, z: -room.depth / 2 }
    const roomMax = { x: room.width / 2, z: room.depth / 2 }

    // 4つの角のいずれかが部屋の境界外にある場合true
    return corners.some(corner =>
        corner.x < roomMin.x ||
        corner.x > roomMax.x ||
        corner.z < roomMin.z ||
        corner.z > roomMax.z
    )
}

/**
 * 全家具の衝突状態をチェック
 * @param {Array} furnitureList - 家具のリスト
 * @param {Object} room - 部屋の寸法
 * @returns {Object} - { collisions: Map, outOfBounds: Set }
 */
export function checkAllCollisions(furnitureList, room) {
    const collisions = new Map() // 家具ID -> 衝突している家具IDのSet
    const outOfBounds = new Set() // 部屋からはみ出している家具ID

    // 各家具について部屋の境界チェック
    furnitureList.forEach((furniture) => {
        if (checkRoomBoundary(furniture, room)) {
            outOfBounds.add(furniture.id)
        }
    })

    // 家具同士の衝突チェック
    for (let i = 0; i < furnitureList.length; i++) {
        for (let j = i + 1; j < furnitureList.length; j++) {
            if (checkCollision(furnitureList[i], furnitureList[j])) {
                // 衝突を記録
                if (!collisions.has(furnitureList[i].id)) {
                    collisions.set(furnitureList[i].id, new Set())
                }
                if (!collisions.has(furnitureList[j].id)) {
                    collisions.set(furnitureList[j].id, new Set())
                }
                collisions.get(furnitureList[i].id).add(furnitureList[j].id)
                collisions.get(furnitureList[j].id).add(furnitureList[i].id)
            }
        }
    }

    return { collisions, outOfBounds }
}

/**
 * 家具が干渉しているかチェック（衝突 or 境界外）
 * @param {string} furnitureId - 家具ID
 * @param {Object} collisionResult - checkAllCollisionsの結果
 * @returns {boolean} - 干渉している場合true
 */
export function hasInterference(furnitureId, collisionResult) {
    return (
        collisionResult.outOfBounds.has(furnitureId) ||
        (collisionResult.collisions.has(furnitureId) &&
            collisionResult.collisions.get(furnitureId).size > 0)
    )
}

/**
 * 新しい家具を配置できる空きスペースを探索
 * @param {Object} newFurnitureSize - { width, depth, height }
 * @param {Array} existingFurniture - 既存の家具リスト
 * @param {Object} room - { width, depth, height }
 * @param {number} rotation - 新しい家具の回転角度（デフォルト0）
 * @returns {Object} - { x, z, hasSpace } 配置位置と空きがあったかどうか
 */
export function findEmptyPosition(newFurnitureSize, existingFurniture, room, rotation = 0) {
    // 回転角度に応じて有効サイズを計算
    const isRotated90 = Math.abs(rotation % 180) === 90
    const effectiveWidth = isRotated90 ? newFurnitureSize.depth : newFurnitureSize.width
    const effectiveDepth = isRotated90 ? newFurnitureSize.width : newFurnitureSize.depth

    // グリッドのステップサイズ (30cm刻み)
    const gridStep = 30

    // 配置可能な範囲を計算
    const halfRoomWidth = room.width / 2
    const halfRoomDepth = room.depth / 2
    const halfFurnitureWidth = effectiveWidth / 2
    const halfFurnitureDepth = effectiveDepth / 2

    const minX = -halfRoomWidth + halfFurnitureWidth
    const maxX = halfRoomWidth - halfFurnitureWidth
    const minZ = -halfRoomDepth + halfFurnitureDepth
    const maxZ = halfRoomDepth - halfFurnitureDepth

    // 家具が既存の家具と衝突するかチェックする関数
    const checkPositionCollision = (x, z) => {
        const testFurniture = {
            position: { x, y: 0, z },
            size: newFurnitureSize,
            rotation: rotation
        }

        // 部屋の境界チェック
        if (checkRoomBoundary(testFurniture, room)) {
            return true // 衝突（境界外）
        }

        // 既存家具との衝突チェック
        for (const existing of existingFurniture) {
            if (checkCollision(testFurniture, existing)) {
                return true // 衝突
            }
        }

        return false // 衝突なし
    }

    // 候補位置リスト（中央から外側に向かって探索）
    const candidates = []

    // スパイラル探索: 中央から外側に向かって候補を生成
    for (let radius = 0; radius <= Math.max(halfRoomWidth, halfRoomDepth); radius += gridStep) {
        // 中央 (radius=0)
        if (radius === 0) {
            candidates.push({ x: 0, z: 0 })
            continue
        }

        // 現在の半径で正方形の外周上の点を追加
        for (let x = -radius; x <= radius; x += gridStep) {
            candidates.push({ x, z: -radius }) // 上辺
            candidates.push({ x, z: radius })  // 下辺
        }
        for (let z = -radius + gridStep; z < radius; z += gridStep) {
            candidates.push({ x: -radius, z }) // 左辺
            candidates.push({ x: radius, z })  // 右辺
        }
    }

    // 範囲内の候補をフィルタリング
    const validCandidates = candidates.filter(pos =>
        pos.x >= minX && pos.x <= maxX &&
        pos.z >= minZ && pos.z <= maxZ
    )

    // 中央からの距離でソート
    validCandidates.sort((a, b) => {
        const distA = Math.sqrt(a.x * a.x + a.z * a.z)
        const distB = Math.sqrt(b.x * b.x + b.z * b.z)
        return distA - distB
    })

    // 空いている位置を探す
    for (const pos of validCandidates) {
        if (!checkPositionCollision(pos.x, pos.z)) {
            return { x: pos.x, z: pos.z, hasSpace: true }
        }
    }

    // 空きスペースがない場合、原点付近に配置
    return { x: 0, z: 0, hasSpace: false }
}
